/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable react-hooks/rules-of-hooks */
import * as React from 'react';
import styled from 'styled-components';
import { SYS_SPACE_RELATIVE_SM, SYS_SPACE_RELATIVE_XS } from '@oneaudi/unified-web-common';
import { Text } from '@oneaudi/unified-web-components';
import {
  renderTextWithFootnotesReferencesV2,
  cleanupEmissionConsumptionMarkup,
  getConsumptionsAndEmissionsWithIdentifiers,
} from '@oneaudi/feature-app-utils';
import type { VueFormatterServiceInterfaceV1 } from '@oneaudi/vue-formatter-service';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import type { Logger } from '@feature-hub/logger';
import { debug } from '../../utils/debugging';

type TextVariant = {
  type: 'copy';
  order: '1' | '2' | '3';
  style: 'bold' | 'normal' | 'short';
};

export interface ConsumptionAndEmissionsProps {
  caeIds?: string[];
  onCaeReady?: () => void;
  vueFormatterService: VueFormatterServiceInterfaceV1;
  localeService: LocaleServiceV1;
  loggerService?: Logger;
}

interface CaeData {
  consumption: string | React.ReactNode;
  emissions: string | React.ReactNode;
  emissionClass: string | React.ReactNode;
  dischargedCO2Class?: string | React.ReactNode;
  dischargedConsumption?: string | React.ReactNode;
}

const StyledConsumptionAndEmissions = styled.div`
  padding-top: ${process.env.IS_LEGACY ? SYS_SPACE_RELATIVE_XS : SYS_SPACE_RELATIVE_SM};
`;

export const ConsumptionAndEmissions: React.FC<ConsumptionAndEmissionsProps> = ({
  caeIds,
  onCaeReady,
  vueFormatterService,
  localeService,
  loggerService,
}) => {
  if (!caeIds || !caeIds.length) return null;

  const [caeData, setCaeData] = React.useState<CaeData[]>();

  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    (async () => {
      const consumptionAndEmissionData = await getConsumptionsAndEmissionsWithIdentifiers(
        caeIds,
        vueFormatterService,
        localeService,
        loggerService,
      );

      if (caeIds && caeIds.length) {
        debug(`CaE loaded: [${caeIds.join(',')}] / ${JSON.stringify(consumptionAndEmissionData)}`);
      }

      const consumptionAndEmissionArray =
        consumptionAndEmissionData && Object.keys(consumptionAndEmissionData).length > 0
          ? Object.values(consumptionAndEmissionData)
          : undefined;

      const _caeData: CaeData[] | undefined =
        consumptionAndEmissionArray &&
        consumptionAndEmissionArray
          .filter((cae) => {
            return (
              cae?.formattedConsumption ||
              cae?.formattedEmission ||
              cae?.formattedCo2Class ||
              cae?.formattedDischargedConsumption ||
              cae?.formattedDischargedCo2Class
            );
          })
          .map((cae) => {
            return {
              consumption: cae.formattedConsumption
                ? renderTextWithFootnotesReferencesV2(
                    cleanupEmissionConsumptionMarkup(cae.formattedConsumption),
                  )
                : '',
              emissions: cae.formattedEmission
                ? renderTextWithFootnotesReferencesV2(
                    cleanupEmissionConsumptionMarkup(cae.formattedEmission),
                  )
                : '',
              emissionClass: cae.formattedCo2Class
                ? renderTextWithFootnotesReferencesV2(
                    cleanupEmissionConsumptionMarkup(cae.formattedCo2Class),
                  )
                : '',
              dischargedConsumption: cae.formattedDischargedConsumption
                ? renderTextWithFootnotesReferencesV2(
                    cleanupEmissionConsumptionMarkup(cae.formattedDischargedConsumption),
                  )
                : '',
              dischargedCO2Class: cae.formattedDischargedCo2Class
                ? renderTextWithFootnotesReferencesV2(
                    cleanupEmissionConsumptionMarkup(cae.formattedDischargedCo2Class),
                  )
                : '',
            };
          });
      if (_caeData?.length) setCaeData(_caeData);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (caeData?.length) onCaeReady?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caeData?.length]);

  if (!caeData?.length) return null;

  // Solution to prettier's line-break and eslint's next-line rule confilict
  const variant: TextVariant = { type: 'copy', order: '2', style: 'normal' };

  return (
    <StyledConsumptionAndEmissions>
      {caeData.map((x, i) => (
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        <Text key={['a', i, x.emissionClass].join('_')} variant={variant}>
          {x.consumption}
          {x.emissions && <span>; {x.emissions}</span>}
          {x.emissionClass && <span>; {x.emissionClass}</span>}
          {x.dischargedConsumption && <span>; {x.dischargedConsumption}</span>}
          {x.dischargedCO2Class && <span>; {x.dischargedCO2Class}</span>}
        </Text>
      ))}
    </StyledConsumptionAndEmissions>
  );
};

ConsumptionAndEmissions.displayName = 'ConsumptionAndEmissions';
